import { type FormikProps, withFormik } from "formik";
import { type FC, useEffect, useRef } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type AppDispatch, type RootState } from "../../../../Application/globaltypes/redux";
import { type PackSettingsPayload, type PackSettingsView } from "../../types/state";
import { type PackSettingsProps } from "../types";
import validationSchemas from "../../../../../utils/validationSchemas";
import { type AutosaveProps, withAutosave } from "../../../../../utils/withAutosave";
import { packTrialPeriodsSelector } from "../../state/slices/packTrialPeriodsSlice";
import { fetchPackTrialPeriods } from "../../state/thunks/packTrialPriodsThunk";
import { fetchPackBillingTerms } from "../../state/thunks/packBillingTermsThunk";
import { noop } from "lodash";
import { fetchPackPricingTypes } from "../../state/thunks/packPricingTypesThunk";
import SettingsForm from "./SettingsForm";
import { packBillingTermsSelector } from "../../state/slices/packBillingTermsSlice";
import { fetchPackSettings, updatePackSettings } from "../../state/thunks/packSettingsThunk";
import { bindAction } from "../../../../../interfaces/redux";
import { useEventCallback } from "hooks/useEventCallback";
import { reset } from "features/Licensing/Packs/state/slices/packSettingsSlice";
import { packPricingTypesSelector, reset as resetPricingTypes } from "../../state/slices/packPricingTypesSlice";
import { hasPacksManagePermissionSelector } from "../../state/selectors";
import { PricingTypes } from "enums/licensing/pricingTypes";

export type SettingsAllProps = PackSettingsProps & PropsFromRedux & FormikProps<PackSettingsView> & AutosaveProps;

const PackSettingsTab: FC<SettingsAllProps> = (props: SettingsAllProps) => {
  const {
    id,
    disabled,
    isLoading,
    isSettingsLoading,
    billingTerms,
    pricingTypes,
    packTrialPeriods,
    settings,
    values,
    fetchPackTrialPeriods,
    fetchPackBillingTerms,
    fetchPackPricingTypes,
    fetchPackSettings,
    acceptHandlers,
    updatePackSettings,
    reset,
    resetPricingTypes,
    accountId,
    hasGlobalAccess,
    hasPacksManagePermissions,
    ...rest
  } = props;

  useEffect(() => {
    if (!id) {
      return;
    }

    fetchPackSettings(id);
    fetchPackTrialPeriods();
    fetchPackBillingTerms();
    fetchPackPricingTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    props.onIsValidChange?.(props.isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isValid, props.onIsValidChange]);

  const saveSettings = useEventCallback(() => {
    return isValid(props) && updatePackSettings(mapToPayload(valuesRef.current, props));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isValid, id, props.isValidating, props.isDraft, updatePackSettings]);

  useEffect(() => {
    acceptHandlers?.({
      onNext: saveSettings,
      onPrevious: saveSettings,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      reset();
      resetPricingTypes();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    props.isSettingsLoaded && (
      <SettingsForm
        entityId={id}
        disabled={disabled}
        isLoading={isLoading || isSettingsLoading}
        billingTerms={billingTerms}
        packTrialPeriods={packTrialPeriods}
        pricingTypes={pricingTypes}
        values={values}
        hasGlobalAccess={hasGlobalAccess}
        hasPacksManagePermissions={hasPacksManagePermissions}
        {...rest}
      />
    )
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState, ownProps: PackSettingsProps) => ({
  id: ownProps.entityId || state.packs.packEntityStateReducer.entityId,
  isDraft: state.packs.packDetailsReducer.pack.isDraft,
  settings: state.packs.packSettingsReducer.settings,
  packTrialPeriods: packTrialPeriodsSelector(state),
  billingTerms: packBillingTermsSelector(state),
  pricingTypes: packPricingTypesSelector(state),
  isCreating: false,
  isSettingsLoading: state.packs.packSettingsReducer.isLoading,
  isSettingsLoaded: state.packs.packSettingsReducer.isLoaded,
  isLoading: state.packs.packDetailsReducer.isLoading,
  isLoaded: state.packs.packDetailsReducer.isPackLoaded,
  accountId: state.userProfile.accountId,
  hasGlobalAccess: state.userProfile.hasGlobalAccess,
  hasPacksManagePermissions: hasPacksManagePermissionSelector(state),
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updatePackSettings: bindAction(updatePackSettings, dispatch),
  fetchPackSettings: bindAction(fetchPackSettings, dispatch),
  fetchPackTrialPeriods: bindAction(fetchPackTrialPeriods, dispatch),
  fetchPackBillingTerms: bindAction(fetchPackBillingTerms, dispatch),
  fetchPackPricingTypes: bindAction(fetchPackPricingTypes, dispatch),
  reset: bindAction(reset, dispatch),
  resetPricingTypes: bindAction(resetPricingTypes, dispatch),
});

const mapToPayload = (
  values: PackSettingsView,
  props: Omit<SettingsAllProps, keyof AutosaveProps>,
): PackSettingsPayload => ({
  id: props.id,
  isFree: props.usePricingTypes ? undefined : values.isFree,
  pricingTypeId: props.usePricingTypes ? values.pricingTypeId : undefined,
  licensingSettings: values.pricingTypeId !== PricingTypes.Paid ? values.licensingSettings : undefined,
  pricingSettings:
    values.pricingTypeId === PricingTypes.Paid
      ? {
          ...values.pricingSettings,
          pricePerUser: parseFloat(parseFloat((values.pricingSettings.pricePerUser ?? 0).toString()).toFixed(2)),
        }
      : undefined,
});

const normalize = (values: PackSettingsView): PackSettingsView => ({
  ...values,
  pricingSettings: {
    ...values.pricingSettings,
    pricePerUser: !values.pricingSettings.pricePerUser
      ? values.pricingSettings.pricePerUser
      : parseFloat(values.pricingSettings.pricePerUser.toString()),
  },
});

const isValid = (props: Omit<SettingsAllProps, keyof AutosaveProps>) =>
  props.isDraft && props.isValid && !props.isValidating && props.id > 0;

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

/* istanbul ignore next */
const withAutosaveComponent = withAutosave<SettingsAllProps, PackSettingsView, PackSettingsView, PackSettingsPayload>({
  getInitValues: (props) => normalize(props.settings),
  stateProvider: (values) => normalize(values),
  entityUpdater: (props) => props.updatePackSettings,
  isValid,
  stateProjector: mapToPayload,
  isStateLoaded: (_, props) => props.isLoaded && props.isSettingsLoaded,
  isDeffered: (props) => !props.isLoaded || !props.isSettingsLoaded,
})(PackSettingsTab);

/* istanbul ignore next */
const component = withFormik<PackSettingsProps & PropsFromRedux, PackSettingsView>({
  validationSchema: validationSchemas.packSettings,
  enableReinitialize: true,
  mapPropsToValues: (props: PackSettingsProps & PropsFromRedux) => props.settings,
  handleSubmit: noop,
})(withAutosaveComponent);

export default connect(mapStateToProps, mapDispatchToProps)(component);
